import {
  AuthPlatform,
  UserAccount as UserAccountBase,
  User as UserBase,
  UserWithAdditionalInfo as UserWithAdditionalInfoBase,
} from '@fabric-ui/components';
import { type AnyRouter } from '@tanstack/react-router';
import { JWT } from 'aws-amplify/auth';

export { AuthPlatform } from '@fabric-ui/components';
export interface AuthConfiguration {
  userPoolId: string;
  userPoolWebClientId: string;
  userPoolRegion: string;
  userPoolDomain: string;
}

export interface AuthConfigurationV2 {
  connectionDetails: CognitoConnectionDetails | ZidConnectionDetails;
  provider: AuthPlatform;
}

export interface CognitoConnectionDetails {
  userPoolId: string;
  userPoolWebClientId: string;
  userPoolRegion: string;
  userPoolDomain: string;
}

export interface ZidConnectionDetails {
  zidTenantId: string;
  zidTenantServiceDomain: string;
  serviceId: string;
  insecure: boolean;
}

// Backend response that supports both old and new formats
export interface AuthenticationResponse {
  authenticationResponse?: TokenResponse; // New format (unified)
  zidAuthenticationResponse?: TokenResponse; // Old format (legacy - deprecated)
  session: UserSession;
}

export interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

/**
 * Normalizes authentication response to handle both old and new backend formats.
 * Supports transition period where old backend returns zidAuthenticationResponse
 * and new backend returns authenticationResponse.
 */
export const normalizeAuthenticationResponse = (
  response: AuthenticationResponse
): {
  tokens: TokenResponse;
  session: UserSession;
} => {
  // Try new format first, fallback to old format
  const tokens = response.authenticationResponse || response.zidAuthenticationResponse;

  if (!tokens) {
    throw new Error('Invalid authentication response: missing token data');
  }

  return {
    tokens,
    session: response.session,
  };
};

export interface UserSession {
  userId: string;
  externalUserId: string;
  accountId: string;
  roleName: string;
  machine: boolean;
  email: string;
  permissions: string;
  userName: string;
  userRoleId: string;
  accountApps: string[];
  accountType: string;
  accountName: string;
  ziaCloudName: string;
  ziaCompanyId: string;
  sessionType: string;
  jwtId: string;
  mainAccountId: string;
  currentAccountCustomerMainAccount: string;
}

export interface AuthSession {
  tokens?: AuthTokens;
  session?: UserInfo;
  error?: any;
}

export interface AuthTokens {
  idToken?: JWT;
  accessToken?: JWT;
}

export type TokenUser = {
  email: string;
  avalor_user_id: string;
  user_role_id: string;
  account_id: string;
  abac_filters: string;
  scope: string;
  avalor_user_name: string;
  role_name: string;
};

export type UserInfo = {
  email: string;
  userId: string;
  roleId: string;
  accountId: string;
  abac: string;
  permissions: string;
  userName: string;
  roleName: string;
  mainAccountId?: string;
  currentAccountCustomerMainAccount?: string;
};

export const convertSessionToUserInfo = (session: UserSession): UserInfo => ({
  email: session.email,
  userId: session.userId,
  roleId: session.userRoleId,
  accountId: session.accountId,
  currentAccountCustomerMainAccount: session.currentAccountCustomerMainAccount,
  abac: '',
  permissions: session.permissions,
  userName: session.userName,
  roleName: session.roleName,
  mainAccountId: session.mainAccountId,
});

export type SessionInfo = {
  error?: any;
  accountToken?: JWT;
  userInfo?: UserInfo;
  defaultAccountId?: string;
};

export type AuthenticationProvider = {
  configureAuthProvider(router?: AnyRouter): void;
  signIn(state?: string): Promise<void>;
  overrideAccountId(accountId: string, attempts?: number): Promise<void>;
  fetchSession(object: { forceRefresh?: boolean; firstLogin?: boolean; accountId?: string }): Promise<SessionInfo>;
  signOut(): void;
  clearLS(): void;
  isUserAuthenticated(): Promise<void>;
  processAuthRedirect(router?: AnyRouter): Promise<void> | void;
  getAccountFromToken(decodedToken: JWT): string;
  formatLocalSessionObject({ jwt, session }): UserInfo;
  getAdditionalUserData: string;
  provider: AuthPlatform;
};

export type User = UserBase;
export type UserAccount = UserAccountBase;
export type AuthStateType = { user: User; isAuthenticated: boolean };
export type UserWithAdditionalInfo = UserWithAdditionalInfoBase;
